import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';
import { projects } from '../../utils/constants';
import { FaArrowLeft } from 'react-icons/fa';



const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();


  // Call useScrollReveal hook at the top level
   useScrollReveal();
  
  // Find the project by ID
  const project = projects.find(p => p.id === parseInt(id));
  
  // Scroll to top when component mounts or ID changes
   useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Show error if project not found
  if (!project) {
    return (
      <div className="project-not-found" style={{
        textAlign: 'center',
        padding: '4rem',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#333' }}>
          Project Not Found
        </h2>
        <p style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#666' }}>
          The requested project with ID "{id}" could not be found.
        </p>
        <p style={{ fontSize: '1rem', marginBottom: '2rem', color: '#888' }}>
          Available projects: {projects.map(p => p.id).join(', ')}
        </p>
        <button 
          onClick={() => navigate('/work')} 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'all 0.3s ease',
            textDecoration: 'none'
          }}
          onMouseOver={(e) => e.target.style.background = '#3730a3'}
          onMouseOut={(e) => e.target.style.background = '#4f46e5'}
        >
          <FaArrowLeft /> Back to Projects
        </button>
      </div>
    );
  }

  return (
    <main className="project-detail" style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '2rem 1rem',
      color: '#333'
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate('/work')} 
        className="back-button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '2rem',
          padding: '0.75rem 1.5rem',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '1rem',
          transition: 'all 0.3s ease',
          color: '#475569'
        }}
        onMouseOver={(e) => {
          e.target.style.background = '#e2e8f0';
          e.target.style.transform = 'translateX(-2px)';
        }}
        onMouseOut={(e) => {
          e.target.style.background = '#f8fafc';
          e.target.style.transform = 'translateX(0)';
        }}
      >
        <FaArrowLeft /> Back to Projects
      </button>

      {/* Hero Section */}
      <section className="project-hero" style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '3rem',
        marginBottom: '4rem'
      }}>
        <div className="project-hero__image" style={{
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          height: '400px'
        }}>
          <img 
            src={project.image} 
            alt={project.title} 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block'
            }}
            loading="eager"
          />
        </div>
        
        <div className="project-hero__content" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3rem)', 
            marginBottom: '1rem',
            fontWeight: '700',
            lineHeight: '1.2',
            color: '#1a202c'
          }}>
            {project.title}
          </h1>
          
          <p className="project-subtitle" style={{ 
            fontSize: '1.25rem', 
            color: '#4a5568', 
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            {project.description}
          </p>
          
          {/* Project Meta Information */}
          <div className="project-meta" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem',
            padding: '1.5rem',
            background: '#f7fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            {project.client && (
              <div className="project-meta__item">
                <span style={{ 
                  display: 'block',
                  fontSize: '0.875rem',
                  color: '#718096',
                  marginBottom: '0.25rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Client
                </span>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  color: '#2d3748',
                  margin: '0'
                }}>
                  {project.client}
                </p>
              </div>
            )}
            
            {project.duration && (
              <div className="project-meta__item">
                <span style={{ 
                  display: 'block',
                  fontSize: '0.875rem',
                  color: '#718096',
                  marginBottom: '0.25rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Duration
                </span>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  color: '#2d3748',
                  margin: '0'
                }}>
                  {project.duration}
                </p>
              </div>
            )}
            
            {project.team && (
              <div className="project-meta__item">
                <span style={{ 
                  display: 'block',
                  fontSize: '0.875rem',
                  color: '#718096',
                  marginBottom: '0.25rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Team Size
                </span>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  color: '#2d3748',
                  margin: '0'
                }}>
                  {project.team}
                </p>
              </div>
            )}
          </div>
          
          {/* Technologies */}
          <div className="project-tech" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            {project.tags.map(tag => (
              <span 
                key={tag} 
                className="tech-tag"
                style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '25px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  border: 'none'
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="project-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem'
        }}>
          The Challenge
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <p style={{ 
          fontSize: '1.125rem', 
          lineHeight: '1.8', 
          color: '#4a5568',
          marginBottom: '0'
        }}>
          {project.challenge}
        </p>
      </section>

      {/* Solution Section */}
      <section className="project-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem'
        }}>
          Our Solution
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <p style={{ 
          fontSize: '1.125rem', 
          lineHeight: '1.8', 
          color: '#4a5568',
          marginBottom: '0'
        }}>
          {project.solution}
        </p>
      </section>

      {/* Key Features Section */}
      <section className="project-section" style={{ marginBottom: '3rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem'
        }}>
          Key Features
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <ul className="project-features" style={{
          listStyleType: 'none',
          paddingLeft: '0',
          display: 'grid',
          gap: '1rem'
        }}>
          {project.details.map((detail, index) => (
            <li 
              key={index} 
              style={{
                position: 'relative',
                paddingLeft: '2rem',
                fontSize: '1.125rem',
                lineHeight: '1.7',
                color: '#4a5568',
                display: 'flex',
                alignItems: 'flex-start'
              }}
            >
              <span style={{
                position: 'absolute',
                left: '0',
                top: '0.125rem',
                width: '20px',
                height: '20px',
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                ✓
              </span>
              {detail}
            </li>
          ))}
        </ul>
      </section>

      {/* Results Section */}
      <section className="project-section project-results" style={{ marginBottom: '4rem' }}>
        <h2 style={{ 
          fontSize: '1.875rem', 
          marginBottom: '1.5rem',
          fontWeight: '700',
          color: '#1a202c',
          position: 'relative',
          paddingBottom: '0.75rem'
        }}>
          Results & Impact
          <div style={{
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #4f46e5, #7c3aed)',
            borderRadius: '2px'
          }}></div>
        </h2>
        <div className="results-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          {project.results.map((result, index) => (
            <div 
              key={index} 
              className="result-card"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e2e8f0',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(79, 70, 229, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
              }}
            >
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: 'linear-gradient(90deg, #4f46e5, #7c3aed)'
              }}></div>
              <p style={{ 
                margin: '0', 
                lineHeight: '1.6',
                fontSize: '1.125rem',
                color: '#2d3748',
                fontWeight: '500'
              }}>
                {result}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="project-cta" style={{
        textAlign: 'center',
        padding: '3rem 2rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        color: 'white',
        marginBottom: '2rem'
      }}>
        <h3 style={{
          fontSize: '1.5rem',
          marginBottom: '1rem',
          fontWeight: '600'
        }}>
          Ready to Start Your Project?
        </h3>
        <p style={{
          fontSize: '1.125rem',
          marginBottom: '2rem',
          opacity: '0.9'
        }}>
          Let's discuss how we can help bring your vision to life.
        </p>
        <button
          onClick={() => navigate('/contact')}
          style={{
            padding: '1rem 2rem',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)'
          }}
          onMouseOver={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          Get In Touch
        </button>
      </section>
    </main>
  );
};

export default ProjectDetail;